<script setup>
import { storeToRefs } from 'pinia'
import Locator from 'assets/icons/locator.svg'
import Up from 'assets/icons/up.svg'
import SkeletonPlaceholder from '@/components/Base/SkeletonPlaceholder.vue'
import { useMessageStore } from '~/stores/messages'

const props = defineProps({
    parkingPaymentInfo: {
        type: Object,
        default: () => ({}),
        validator(rawValue) {
            const validations = {
                center: rawValue => rawValue.center === undefined || typeof rawValue.center === 'string',
                startDate: rawValue => rawValue.startDate === undefined || typeof rawValue.startDate === 'string',
                totalPrice: rawValue => rawValue.totalPrice === undefined || typeof rawValue.totalPrice === 'string',
                duration: rawValue => rawValue.duration === undefined || typeof rawValue.duration === 'string',
                pdfLink: rawValue => rawValue.pdfLink === undefined || typeof rawValue.pdfLink === 'string',
                toggle: rawValue => rawValue.toggle === undefined || typeof rawValue.toggle === 'boolean',
                placeholder: rawValue => rawValue.placeholder === undefined || typeof rawValue.placeholder === 'boolean',
            }

            return Object.values(validations).every(validation => validation(rawValue))
        }
    }
})

// Fetch messages
const messagesStore = useMessageStore()
const { messages: staticKeys } = storeToRefs(messagesStore)

// Get the date of entrance
const getEntranceDate = computed(() => {
    if (!props.parkingPaymentInfo?.startDate) return ''

    const date = new Date(props.parkingPaymentInfo?.startDate)

    if (!date) return ''

    // Extract day, month, and year
    const day = date?.getUTCDate()
    const month = date?.getUTCMonth() + 1 // Months are zero-based
    const year = date?.getUTCFullYear()

    // Format day and month with leading zeros if needed
    const formattedDay = day?.toString().padStart(2, '0')
    const formattedMonth = month?.toString().padStart(2, '0')

    return `${formattedDay}.${formattedMonth}.${year}`
})

const getEntranceTime = computed(() => {
    if (!props.parkingPaymentInfo?.startDate) return ''

    const date = new Date(props.parkingPaymentInfo.startDate)

    if (isNaN(date.getTime())) return ''

    // Convert UTC date to local time in Europe/Berlin
    const options = {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Berlin', // Auto-adjusts for CET/CEST
        hour12: false // Use 24-hour format
    }

    return new Intl.DateTimeFormat('de-DE', options).format(date) + ` ${staticKeys.value?.general_clock}`
})

// Define some refs
const open = ref(false)
const height = ref('0px')
const toggleContent = ref(null)
const bp = 768

const setHeight = () => {
    height.value = window.innerWidth <= bp ? open.value ? `${toggleContent.value.scrollHeight}px` : '0' : 'auto'
}
/**
 * Toggles the item by switching the open value and setting the height value to the scroll height of the toggleContent value.
 *
 * @return None
 */
const toggleItem = () => {
    open.value = !open.value
    setHeight()
}

// Change height item on resize
if (import.meta.client && props.parkingPaymentInfo?.toggle) {
    let timeout = false

    window.addEventListener('resize', () => {
        clearTimeout(timeout)
        timeout = setTimeout(setHeight, 250)
    })
}

const getWrapperClass = computed(() => {
    return {
        'parking-payment-info-open': open.value,
        'parking-payment-toggle': props.parkingPaymentInfo?.toggle,
    }
})
const getToggleLabel = computed(() => open.value ? staticKeys.value?.parkinghistory_less : staticKeys.value?.parkinghistory_more)
</script>

<template>
    <div class="parking-payment-info-wrapper" :class="getWrapperClass">
        <header class="parking-payment-info-header">
            <div class="parking-payment-info-locator">
                <component :is="SkeletonPlaceholder" v-if="parkingPaymentInfo.placeholder" :skeleton-placeholder-config="{ type: 'image', width: '50px', height: '50px' }" />
                <IconWrap v-else>
                    <component :is="Locator" />
                </IconWrap>
            </div>

            <component :is="SkeletonPlaceholder" v-if="parkingPaymentInfo.placeholder" :skeleton-placeholder-config="{ type: 'headline', iterator: 1 }" />

            <h3 v-else class="parking-payment-info-location">
                {{ parkingPaymentInfo.center }}
            </h3>

            <component :is="SkeletonPlaceholder" v-if="parkingPaymentInfo.placeholder" :skeleton-placeholder-config="{ type: 'text', iterator: 1 }" />

            <p v-else class="parking-payment-info-date">
                {{ getEntranceDate }}
            </p>

            <p v-if="! parkingPaymentInfo.placeholder" class="parking-payment-info-amount">
                {{ parkingPaymentInfo.totalPrice }}
            </p>
        </header>

        <component :is="SkeletonPlaceholder" v-if="parkingPaymentInfo.placeholder" class="parking-payment-info-footer" :skeleton-placeholder-config="{ type: 'text', iterator: 1 }" />

        <h3
            v-if="parkingPaymentInfo.toggle && ! parkingPaymentInfo.placeholder"
            class="parking-payment-info-toggle"
            @click="toggleItem"
        >
            {{ getToggleLabel }}
            <Up />
        </h3>

        <footer ref="toggleContent" class="parking-payment-info-footer">
            <div v-if="! parkingPaymentInfo.placeholder" class="parking-payment-info-time">
                <h4>{{ staticKeys.parking_history_entry_time }}</h4>
                {{ getEntranceTime }}
            </div>

            <div v-if="! parkingPaymentInfo.placeholder" class="parking-payment-info-duration">
                <h4>{{ staticKeys.parking_history_parking_duration }}</h4>
                {{ parkingPaymentInfo.duration }}
            </div>

            <div v-if="parkingPaymentInfo.pdfLink" class="parking-payment-info-cta">
                <BaseButton
                    :base-button-config="{
                        buttonType: 'secondary',
                        type: 'file',
                        link: parkingPaymentInfo.pdfLink,
                        active: true,
                        large: true,
                        placeholder: parkingPaymentInfo.placeholder
                    }"
                >
                    {{ staticKeys.parkinghistory_download_cta }}
                </BaseButton>
            </div>
        </footer>
    </div>
</template>

<style lang="scss">
.parking-payment-info-wrapper {
    width: 100%;
}

.parking-payment-info-header {
    display: grid;
    grid-template: auto auto auto / 65px auto 1fr;
    grid-template-areas: "locator headline headline"
        "locator date amount";
    align-items: center;
    margin-block-end: px2rem(15);
    padding-block-end: px2rem(15);
    border-bottom: 2px solid $parking-payment-header-border-color;
}

.parking-payment-info-locator {
    grid-area: locator;
}

.parking-payment-info-location {
    grid-area: headline;
    margin-block: px2rem(3);
    padding: 0;

    @include body3-extrabold;
}

.parking-payment-info-date {
    font-family: $font-body;
    font-weight: 500;
    font-size: px2rem(18);
    grid-area: date;
    margin: 0;
}

.parking-payment-info-amount {
    font-family: $font-body;
    font-weight: 500;
    font-size: px2rem(18);
    grid-area: amount;
    text-align: right;
    white-space: nowrap;
    margin: 0;
}

.parking-payment-info-toggle {
    @include body3-medium;

    grid-area: header;
    position: relative;
    cursor: pointer;
    margin-block: px2rem(-20);
    padding-block: px2rem(20);
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    color: $parking-payment-footer-color;

    @include bp(medium1) {
        display: none;
    }

    svg {
        width: 16px;
        transition: transform 600ms ease-in-out;
        transform: rotate(180deg);

        .parking-payment-info-open & {
            transform: rotate(0);
        }
    }
}

.parking-payment-info-footer {
    display: grid;
    grid-template: auto auto / 1fr auto;
    grid-template-areas:
        "time duration"
        "cta cta";
    align-items: center;

    .parking-payment-toggle & {
        height: v-bind('height');
        transition: height 600ms ease-in-out;
        overflow: hidden;

        @include bp(medium1) {
            height: auto;
        }
    }

    h4 {
        @include body3-extrabold;

        margin-block: 0 px2rem(10);
        color: $parking-payment-footer-color;
    }
}

.parking-payment-info-time {
    grid-area: time;
    font-family: $font-body;
    font-weight: 500;
    font-size: px2rem(18);

    .parking-payment-toggle & {
        margin-block-start: px2rem(20);  height: auto;
    }
}

.parking-payment-info-duration {
    grid-area: duration;
    font-family: $font-body;
    font-weight: 500;
    font-size: px2rem(18);
    text-align: right;

    .parking-payment-toggle & {
        margin-block-start: px2rem(20);  height: auto;
    }
}

.parking-payment-info-cta {
    grid-area: cta;
    display: flex;
    justify-content: center;
    margin-block-start: px2rem(30);
}
</style>
